import { useEffect, useMemo, useState } from "react";
import { useNotify } from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { pruefe, sb } from "./daten";
import {
  FELDER,
  csvLesen,
  ibanFinden,
  kopfFinden,
  kopfRaten,
  kopfSignatur,
  xlsxLesen,
  zeilenLesen,
  zuordnungGueltig,
  type Feld as FeldName,
  type GeleseneZeile,
  type Rohzeile,
  type Zuordnung,
} from "./kern/importieren";
import {
  Abschnitt,
  Auswahl,
  Feld,
  Hinweis,
  Tabelle,
  Zahl,
  fmtCent,
  fmtDatum,
  type TabProps,
} from "./teile";

type Datei = { name: string; zeilen: Rohzeile[] };
type Profil = { kopf: string; kopfzeile: number; zuordnung: Zuordnung };

export const ImportTab = ({ daten, neuLaden }: TabProps) => {
  const notify = useNotify();
  const [datei, setDatei] = useState<Datei | null>(null);
  const [kopf, setKopf] = useState(0);
  const [zuordnung, setZuordnung] = useState<Zuordnung>({});
  const [gemerkt, setGemerkt] = useState(false);
  const [kontoId, setKontoId] = useState<number | "">("");
  const [gelesen, setGelesen] = useState<{
    zeilen: GeleseneZeile[];
    uebersprungen: number;
  } | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState("");

  const oeffnen = async (f: File) => {
    setErgebnis("");
    try {
      const roh = await f.arrayBuffer();
      const zeilen = /\.xls[xm]$/i.test(f.name)
        ? await xlsxLesen(roh)
        : csvLesen(roh);
      const { data } = pruefe(await sb().from("bh_spaltenprofil").select("*"));
      const profile = (data ?? []) as Profil[];
      const treffer = zeilen
        .slice(0, 30)
        .findIndex((z) => profile.some((p) => p.kopf === kopfSignatur(z)));
      const auto = kopfFinden(zeilen);
      let start = auto?.kopf ?? kopfRaten(zeilen);
      let z = auto?.zuordnung ?? {};
      if (treffer >= 0) {
        start = treffer;
        z =
          profile.find((p) => p.kopf === kopfSignatur(zeilen[treffer]))
            ?.zuordnung ?? z;
      }
      setGemerkt(treffer >= 0);
      setDatei({ name: f.name, zeilen });
      setKopf(start);
      setZuordnung(z);
      const iban = ibanFinden(f.name, zeilen, start);
      const konto = daten.konten.find((k) => k.iban === iban);
      setKontoId(konto?.id ?? "");
    } catch (e) {
      notify(
        `Datei nicht lesbar: ${e instanceof Error ? e.message : String(e)}`,
        { type: "error" },
      );
    }
  };

  useEffect(() => {
    if (!datei || !zuordnungGueltig(zuordnung)) {
      setGelesen(null);
      return;
    }
    zeilenLesen(datei.zeilen, kopf, zuordnung).then(setGelesen);
  }, [datei, kopf, zuordnung]);

  const spalten = useMemo(
    () => (datei?.zeilen[kopf] ?? []).map((w, i) => `${i + 1}: ${w ?? ""}`),
    [datei, kopf],
  );
  const fremdeIbans = [
    ...new Set(
      (gelesen?.zeilen ?? [])
        .map((z) => z.iban)
        .filter((i): i is string => !!i),
    ),
  ].filter((i) => !daten.konten.some((k) => k.iban === i));
  const brauchtKonto = gelesen?.zeilen.some((z) => !z.iban) ?? false;
  const bereit =
    !!gelesen?.zeilen.length &&
    !fremdeIbans.length &&
    (!brauchtKonto || kontoId !== "");

  const importieren = async () => {
    if (!datei || !gelesen) return;
    setLaeuft(true);
    try {
      pruefe(
        await sb()
          .from("bh_spaltenprofil")
          .upsert({
            kopf: kopfSignatur(datei.zeilen[kopf]),
            kopfzeile: kopf,
            zuordnung,
            updated_at: new Date().toISOString(),
          }),
      );
      const gruppen = new Map<number, GeleseneZeile[]>();
      for (const z of gelesen.zeilen) {
        const id = z.iban
          ? daten.konten.find((k) => k.iban === z.iban)?.id
          : kontoId;
        if (typeof id !== "number") continue;
        gruppen.set(id, [...(gruppen.get(id) ?? []), z]);
      }
      let neu = 0;
      for (const [konto_id, zeilen] of gruppen) {
        const { data: lauf } = pruefe(
          await sb()
            .from("bh_import")
            .insert({
              konto_id,
              dateiname: datei.name,
              zeilen_gesamt: zeilen.length,
              zeilen_neu: 0,
            })
            .select("id")
            .single(),
        );
        let neuImKonto = 0;
        for (let i = 0; i < zeilen.length; i += 500) {
          const { data } = pruefe(
            await sb()
              .from("bh_buchung")
              .upsert(
                zeilen.slice(i, i + 500).map((z) => ({
                  konto_id,
                  import_id: lauf?.id,
                  quelle: "import",
                  schluessel: z.schluessel,
                  datum: z.datum,
                  betrag: z.betrag / 100,
                  partner: z.partner,
                  text: z.text,
                  beleg: z.beleg || null,
                })),
                { onConflict: "konto_id,schluessel", ignoreDuplicates: true },
              )
              .select("id"),
          );
          neuImKonto += data?.length ?? 0;
        }
        pruefe(
          await sb()
            .from("bh_import")
            .update({ zeilen_neu: neuImKonto })
            .eq("id", lauf?.id),
        );
        neu += neuImKonto;
      }
      setErgebnis(
        `${neu} neu, ${gelesen.zeilen.length - neu} schon vorhanden, ${gelesen.uebersprungen} nicht übernommen.`,
      );
      setDatei(null);
      await neuLaden();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="space-y-4">
      <Abschnitt
        titel="Kontoexport einlesen"
        text="CSV oder XLSX. Die Datei bleibt im Browser, gespeichert werden nur die Zeilen. Ein erneuter Import legt nichts doppelt an."
      >
        {daten.konten.length === 0 && (
          <Hinweis art="warnung">
            Bitte zuerst im Setup ein Konto anlegen.
          </Hinweis>
        )}
        <Input
          type="file"
          accept=".csv,.txt,.xlsx,.xlsm"
          disabled={daten.konten.length === 0}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) oeffnen(f);
            e.target.value = "";
          }}
        />
        {ergebnis && <Hinweis>{ergebnis}</Hinweis>}
      </Abschnitt>

      {datei && (
        <Abschnitt
          titel={datei.name}
          text={
            gemerkt
              ? "Spaltenzuordnung von früher übernommen."
              : "Spalten einmal zuordnen, das CRM merkt sie sich."
          }
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Feld name="Kopfzeile (Nr.)">
              <Input
                type="number"
                min={1}
                value={kopf + 1}
                onChange={(e) =>
                  setKopf(Math.max(0, Number(e.target.value) - 1))
                }
              />
            </Feld>
            <Feld name="Konto">
              <Auswahl
                value={kontoId}
                onChange={(e) =>
                  setKontoId(e.target.value ? Number(e.target.value) : "")
                }
              >
                <option value="">bitte wählen</option>
                {daten.konten.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name} ({k.art === "privat" ? "privat" : "geschäftlich"})
                  </option>
                ))}
              </Auswahl>
            </Feld>
            {FELDER.map(({ feld, name }) => (
              <Feld key={feld} name={name}>
                <Auswahl
                  aria-label={`Spalte ${name}`}
                  value={zuordnung[feld] ?? ""}
                  onChange={(e) =>
                    setZuordnung((z) => {
                      const neu = { ...z };
                      if (e.target.value === "") delete neu[feld as FeldName];
                      else neu[feld as FeldName] = Number(e.target.value);
                      return neu;
                    })
                  }
                >
                  <option value="">keine</option>
                  {spalten.map((s, i) => (
                    <option key={i} value={i}>
                      {s}
                    </option>
                  ))}
                </Auswahl>
              </Feld>
            ))}
          </div>
          {!zuordnungGueltig(zuordnung) && (
            <Hinweis art="warnung">
              Datum und Betrag (oder Soll/Haben) müssen zugeordnet sein.
            </Hinweis>
          )}
          {fremdeIbans.length > 0 && (
            <Hinweis art="warnung">
              IBAN nicht im Setup: {fremdeIbans.join(", ")}
            </Hinweis>
          )}
          {gelesen && (
            <>
              <p className="text-sm text-muted-foreground">
                {gelesen.zeilen.length} Zeilen erkannt, {gelesen.uebersprungen}{" "}
                nicht übernommen (nicht ausgeführt oder unlesbar). Vorschau:
              </p>
              <Tabelle
                kopf={["Datum", "Partner", "Text", "Betrag"]}
                rechts={[3]}
              >
                {gelesen.zeilen.slice(0, 5).map((z) => (
                  <tr key={z.schluessel}>
                    <td className="whitespace-nowrap">{fmtDatum(z.datum)}</td>
                    <td>{z.partner}</td>
                    <td>{z.text}</td>
                    <Zahl>{fmtCent(z.betrag)}</Zahl>
                  </tr>
                ))}
              </Tabelle>
            </>
          )}
          <Button disabled={!bereit || laeuft} onClick={importieren}>
            {laeuft ? "Importiert …" : "Importieren"}
          </Button>
        </Abschnitt>
      )}
    </div>
  );
};
